import React from 'react';
import isMobile from '$/utils/isMobile';
import useRunOnce from '$/hooks/useRunOnce';

type Features = 'navbar' | 'toast' | 'title' | 'toolbar';

interface HeaderContextValue {
  headerHeight: number; // height is different when having a sticky banner (toast)
  toastHeight: number; // difference in header height when header have banner
  announcementHeight: number; // difference in header height when header have banner
  baseHeaderHeight: number; // header without banner
  toolbarHeight: number; // height of the toolbar (like preview bar in page editor)
  totalHeight: number;
  hasToast: boolean;
  features: { [key in Features]: boolean | number };
  setFeature: (feature: Features, enabled: boolean, height?: number) => void;
  setHeaderHeightExpanded: (height: number) => void; // expanded height of the header on home page when expanding Tags
}

const HeaderContext = React.createContext<HeaderContextValue>({} as HeaderContextValue);

export const HeaderContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const HEADER_HEIGHT = isMobile ? 45 : 96; /* 6rem * 16 */
  const NAVBAR_HEIGHT = isMobile ? 45 : 0;
  const TOAST_HEIGHT = isMobile ? 60 : 40;
  const TITLE_HEIGHT = isMobile ? 0 : 41;

  const [baseHeaderHeight, setBaseHeaderHeight] = React.useState(HEADER_HEIGHT);
  const [toastHeight, setToastHeight] = React.useState(0);

  const [announcementHeight, setAnnouncementHeight] = React.useState(0);

  const [heightExpanded, setHeightExpanded] = React.useState(0);

  useRunOnce(() => {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function () {
        setAnnouncementHeight(parseInt(document.body.style.marginTop) || 0);
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    setAnnouncementHeight(
      document.body.style.marginTop ? parseInt(document.body.style.marginTop) : 0
    );
  });

  const [features, setFeatures] = React.useState<{ [key in Features]: number | boolean }>({
    toast: false,
    navbar: true,
    title: true,
    toolbar: 0,
  });

  const setFeature = React.useCallback(
    (feature: Features, enabled: boolean, height?: number): void => {
      setFeatures((prev) => {
        if (prev[feature] === enabled) return prev;
        return {
          ...prev,
          [feature]: enabled ? height ?? true : false,
        };
      });
    },
    []
  );

  React.useEffect(() => {
    setBaseHeaderHeight(
      HEADER_HEIGHT +
        (features.navbar ? NAVBAR_HEIGHT : 0) +
        (features.title ? TITLE_HEIGHT : 0) +
        heightExpanded
    );
    setToastHeight(features.toast ? TOAST_HEIGHT : 0);
  }, [TOAST_HEIGHT, HEADER_HEIGHT, NAVBAR_HEIGHT, TITLE_HEIGHT, features, heightExpanded]);

  const setHeaderHeightExpanded = React.useCallback((height: number): void => {
    setHeightExpanded(height);
  }, []);

  const toolbarHeight = features.toolbar ? Number(features.toolbar) : 0;
  const hasToast = toastHeight + announcementHeight !== 0;
  const value = React.useMemo<HeaderContextValue>(() => {
    return {
      hasToast: hasToast,
      features,
      setFeature,
      setHeaderHeightExpanded,
      toolbarHeight,
      toastHeight: toastHeight,
      announcementHeight: announcementHeight,
      baseHeaderHeight: baseHeaderHeight,
      headerHeight: baseHeaderHeight + toastHeight,
      totalHeight: announcementHeight + baseHeaderHeight + toastHeight + toolbarHeight,
    };
  }, [
    hasToast,
    features,
    setFeature,
    setHeaderHeightExpanded,
    toolbarHeight,
    toastHeight,
    announcementHeight,
    baseHeaderHeight,
  ]);

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
};

export const useHeaderContext = (): HeaderContextValue => React.useContext(HeaderContext);
